import { Pipe, PipeTransform } from '@angular/core';

interface ChangeLogRow {
  action: string;
  changes: any;
  item_type: string;
}

@Pipe({name: 'updateCabinTargetingDisplay'})
export class UpdateCabinTargetingDisplayPipe implements PipeTransform {
  private readonly cabinNames = ['Economy', 'Premium Economy', 'Business', 'First'];

  transform(changeHistory: ChangeLogRow[][]): ChangeLogRow[][] {
    return changeHistory.flatMap(changeGroup => this.updateBrandCabinTargetingDisplay(changeGroup));
  }

  private updateBrandCabinTargetingDisplay(changeGroup: ChangeLogRow[]): ChangeLogRow[][] {
    const hasBrandChange = this.hasBrandChange(changeGroup);
    const cabinEntries = changeGroup.filter(logEntry => logEntry.item_type === 'Cabin');
    const hasCabinChanges = cabinEntries.length > 0;
    const hasBrandRemoval = this.hasBrandRemoval(changeGroup);

    if (!hasBrandChange || !hasCabinChanges) {
      return [changeGroup];
    }

    if (hasBrandRemoval) {
      return this.splitBrandRemovalCabinChanges(changeGroup, cabinEntries);
    }

    return this.removeCabinEntries(changeGroup);
  }

  private splitBrandRemovalCabinChanges(changeGroup: ChangeLogRow[], cabinEntries: ChangeLogRow[]): ChangeLogRow[][] {
    const brandEntries = changeGroup.filter(logEntry => logEntry.item_type !== 'Cabin');
    const remainingCabins = this.remainingCabinsFromRemovedCabins(cabinEntries);
    if (remainingCabins.length === 0) {
      return [brandEntries];
    }

    return [
      [{
        ...cabinEntries[0],
        action: 'create',
        changes: { cabin: [null, remainingCabins.join(', ')] },
        item_type: 'Cabin'
      }],
      brandEntries
    ];
  }

  private removeCabinEntries(changeGroup: ChangeLogRow[]): ChangeLogRow[][] {
    return [changeGroup.filter(logEntry => logEntry.item_type !== 'Cabin')];
  }

  private remainingCabinsFromRemovedCabins(cabinEntries: ChangeLogRow[]): string[] {
    const removedCabins = cabinEntries
      .map(logEntry => Array.isArray(logEntry.changes) ? null : logEntry.changes?.cabin?.[0])
      .filter(cabin => cabin);

    return this.cabinNames.filter(cabin => !removedCabins.includes(cabin));
  }

  private hasBrandRemoval(changeGroup: ChangeLogRow[]): boolean {
    return changeGroup.some(logEntry => this.isBrandChange(logEntry) && logEntry.action === 'removed');
  }

  private hasBrandChange(changeGroup: ChangeLogRow[]): boolean {
    return changeGroup.some(logEntry => this.isBrandChange(logEntry));
  }

  private isBrandChange(logEntry: ChangeLogRow): boolean {
    return logEntry.item_type === 'Brand' ||
      !Array.isArray(logEntry.changes) && Object.keys(logEntry.changes || {}).some(key => key.toLowerCase() === 'brand');
  }
}
